/**
 * Fully-qualified domain name (FQDN) validation.
 *
 * Ported to TypeScript from the `validator` package (MIT licensed).
 *
 * @module isFQDN
 */

/**
 * A valid top-level domain: two-or-more letters (including Unicode letters) or
 * a punycode (`xn--…`) label. Numeric-only TLDs deliberately do not match;
 * they are gated separately by `allowNumericTld`.
 */
const VALID_TLD_PATTERN =
  /^([a-z\u00A1-\u00A8\u00AA-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]{2,}|xn[a-z0-9-]{2,})$/i;
/** Full-width characters, which are not allowed in a label. */
const FULL_WIDTH_CHAR_PATTERN = /[\uFF01-\uFF5E]/;
/** A label may not start or end with a hyphen. */
const HYPHEN_PATTERN = /^-|-$/;
/** Allowed characters within a single label. */
const PART_PATTERN = /^[a-z_\u00A1-\uFFFF0-9-]+$/i;
/** Any whitespace, disallowed in a TLD. */
const WHITESPACE_PATTERN = /\s/;
/** A purely numeric label. */
const NUMERIC_PATTERN = /^\d+$/;
const MAX_PART_LENGTH = 63;

/** Tuning flags for {@link isFQDN}. All default to `false` except `requireTld`. */
export interface IsFQDNOptions {
  /** Require a TLD (e.g. reject `localhost`). Defaults to `true`. */
  requireTld?: boolean;
  /** Permit underscores within labels. Defaults to `false`. */
  allowUnderscores?: boolean;
  /** Permit a single trailing dot (the root label). Defaults to `false`. */
  allowTrailingDot?: boolean;
  /** Permit a purely numeric TLD. Defaults to `false`. */
  allowNumericTld?: boolean;
  /** Permit a leading `*.` wildcard label. Defaults to `false`. */
  allowWildcard?: boolean;
  /** Skip the 63-byte per-label length cap. Defaults to `false`. */
  ignoreMaxLength?: boolean;
}

/**
 * Reports whether a string is a valid fully-qualified domain name.
 *
 * @param str - The candidate domain.
 * @param options - See {@link IsFQDNOptions}.
 * @returns `true` if the string is a valid FQDN under the given options.
 *
 * @example
 * ```ts
 * isFQDN("example.com"); // true
 * isFQDN("localhost"); // false
 * isFQDN("localhost", { requireTld: false }); // true
 * isFQDN("*.example.com", { allowWildcard: true }); // true
 * ```
 */
export default function isFQDN(
  str: string,
  {
    requireTld = true,
    allowUnderscores = false,
    allowTrailingDot = false,
    allowNumericTld = false,
    allowWildcard = false,
    ignoreMaxLength = false,
  }: IsFQDNOptions = {},
): boolean {
  /* Remove the optional trailing dot before checking validity */
  if (allowTrailingDot && str.endsWith(".")) {
    str = str.substring(0, str.length - 1);
  }

  /* Remove the optional wildcard before checking validity */
  if (allowWildcard && str.startsWith("*.")) {
    str = str.substring(2);
  }

  const parts = str.split(".");
  const tld = parts[parts.length - 1] ?? "";

  if (requireTld) {
    // disallow fqdns without tld
    if (parts.length < 2) {
      return false;
    }

    if (!allowNumericTld && !VALID_TLD_PATTERN.test(tld)) {
      return false;
    }

    // disallow spaces
    if (WHITESPACE_PATTERN.test(tld)) {
      return false;
    }
  }

  // reject numeric TLDs
  if (!allowNumericTld && NUMERIC_PATTERN.test(tld)) {
    return false;
  }

  return parts.every((part) => {
    if (part.length > MAX_PART_LENGTH && !ignoreMaxLength) {
      return false;
    }

    if (!PART_PATTERN.test(part)) {
      return false;
    }

    // disallow full-width chars
    if (FULL_WIDTH_CHAR_PATTERN.test(part)) {
      return false;
    }

    // disallow parts starting or ending with hyphen
    if (HYPHEN_PATTERN.test(part)) {
      return false;
    }

    if (!allowUnderscores && part.includes("_")) {
      return false;
    }

    return true;
  });
}
